import { useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';
import isPromise from '../../../utils/isPromise';
import normalizeVariables from '../../../graphql/schema/normalizeVariables';
import { handleAlerts, parseErrorResponse } from '../../../utils/parseErrorResponse';
import debounce from 'es6-promise-debounce';
import getClientMutationId from '../../../utils/getClientMutationId';
import ConnectedFormContext from '../../../components/Context/ConnectedFormContext';
import { updateMutationId } from './actions';
import { ConnectedFormContextType } from '../../Context/ConnectedFormContext';

export default function handleTemporarySave<T = any>(
  {
    connectedForm,
    ignoreMutationIdCheck,
  }: { connectedForm: ConnectedFormContextType; ignoreMutationIdCheck?: boolean },
  mutationId: string,
  submittedValues?: T,
  resultCallback?: (result?: any) => void
) {
  const saveOrDelete = 'saved';

  let formValues: T;
  if (!submittedValues) {
    const getSubmitValues = connectedForm.getSubmitValues;
    const connectedFormValues = connectedForm.getValues?.() ?? submittedValues;

    if (getSubmitValues) {
      formValues = getSubmitValues({ values: connectedFormValues }, true);
    } else {
      formValues = connectedFormValues;
    }
  } else {
    formValues = submittedValues;
  }

  const entity = connectedForm.entity;
  const setSubmitFailed = connectedForm.setSubmitFailed;
  const stopSubmit = connectedForm.stopSubmit;
  const formTemporaryUpdate = connectedForm.formTemporaryUpdate;
  const type = entity.type.charAt(0).toLowerCase() + entity.type.slice(1);
  const suppressTemporarySaveUpdate = connectedForm.suppressTemporarySaveUpdate;

  const input: { clientMutationId: string; [key: string]: any } = {
    clientMutationId: mutationId,
  };

  input[type] = normalizeVariables(formValues);
  let result;
  try {
    result = formTemporaryUpdate({
      variables: {
        input,
        temporary: true,
      },
    });
  } catch (submitError: any) {
    const { errors } = submitError;
    const error = parseErrorResponse(errors);
    handleAlerts(error, connectedForm as any, saveOrDelete);
    stopSubmit(error.networkResponse);
    setSubmitFailed();
    if (errors) {
      return error.networkResponse;
    }
    throw submitError;
  }
  if (isPromise(result)) {
    result.then(
      (result: { data: { [key: string]: { result: T } } }) => {
        const objectKeys = Object.keys(result.data);
        if (result.data[objectKeys[0]].result && !suppressTemporarySaveUpdate) {
          connectedForm.temporarySaveSucceeded(
            result.data[objectKeys[0]].result,
            formValues,
            mutationId,
            ignoreMutationIdCheck
          );
        }
        resultCallback?.(result);
        return result;
      },
      (error: any) => {
        let errors = null;
        if (error.networkError && error.networkError.result) {
          errors = error.networkError.result.errors;
        }
        const alert = parseErrorResponse(errors);
        handleAlerts(alert, connectedForm as any, saveOrDelete);
        return error;
      }
    );
  }
  return result;
}

//Helper Hook - Update a field, temporary save it and callback with the result.
export const useSingleFieldTemporarySave = () => {
  const dispatch = useDispatch();
  const connectedForm = useContext(ConnectedFormContext);

  return (field: string, value: any, { postSaveCallback }: { postSaveCallback?: (result?: any) => void } = {}) => {
    const clientMutationId = getClientMutationId();

    Promise.resolve(dispatch(connectedForm.change(field, value, clientMutationId))).then(() => {
      //Trigger temporary save to update the line.
      handleTemporarySave({ connectedForm }, clientMutationId, undefined, postSaveCallback);
    });
  };
};

//Temporary save for the entire form.
export const useTemporarySave = (ignoreMutationIdCheck = false) => {
  const connectedForm = useContext(ConnectedFormContext);
  const dispatch = useDispatch();

  return (action?: any, onTemporaryCompleted?: Function, { mutationId = getClientMutationId() } = {}) => {
    Promise.resolve(dispatch(updateMutationId(connectedForm.form, mutationId))).then(() => {
      handleTemporarySave({ connectedForm, ignoreMutationIdCheck }, mutationId, undefined, undefined).then(
        (result: any) => {
          onTemporaryCompleted?.(result, action);
        }
      );
    });
  };
};

//Debounced temporary save for the entire form (2s default).
export const useDebouncedTemporarySave = (ignoreMutationIdCheck?: boolean, wait?: number = 2000) => {
  const performTemporarySave = useTemporarySave(ignoreMutationIdCheck);
  return useCallback(debounce(performTemporarySave, wait), []);
};
